import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, X, User, Home, ChevronRight, ChevronDown, Info, Stethoscope,
  Activity, FileDown, Filter, RotateCcw, Image as ImageIcon, MapPin, ClipboardList,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '../api/baseapi'; // ✅ นำเข้า api

const useAuth = () => {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  return { user };
};

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  shift: '',
  recordType: '',
  startDate: '',
  endDate: '',
};

// ---------------------------------------------------------------------
// PatientSearch Component (แพทเทิร์นเดียวกับหน้ารายงานสหวิชาชีพ)
// ---------------------------------------------------------------------
function PatientSearch({ visible, onClose, onSelectPatient }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchMode, setSearchMode] = useState('patient');

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setError(null);

    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);

      let response;
      if (searchMode === 'room') {
        response = await api.get(`/service-registrations?room=${encodeURIComponent(query)}`);
      } else {
        response = await api.get(`/service-registrations?search=${encodeURIComponent(query)}`);
      }

      const allResults = response.data.data || [];
      const uniqueResults = allResults.filter((patient, index, self) =>
        index === self.findIndex((p) => p.patient_id === patient.patient_id)
      );

      setSearchResults(uniqueResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  }, [searchMode]);

  const handleSelectPatient = (patient) => {
    onSelectPatient(patient);
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
          <h2 className="text-base sm:text-lg font-bold">ค้นหาผู้ป่วย</h2>
          <div className="w-10" />
        </div>

        <div className="p-3 sm:p-4 bg-gray-50 border-b">
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setSearchMode('patient')}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition ${
                searchMode === 'patient' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <User size={16} />
                <span>ค้นหาชื่อ/HN</span>
              </div>
            </button>
            <button
              onClick={() => setSearchMode('room')}
              className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition ${
                searchMode === 'room' ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 border'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Home size={16} />
                <span>ค้นหาห้อง</span>
              </div>
            </button>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 bg-white border rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
            <Search size={18} className="sm:w-5 sm:h-5 text-gray-500" />
            <input
              type="text"
              className="flex-1 outline-none text-sm sm:text-base"
              placeholder={searchMode === 'room' ? 'ค้นหาเลขห้อง เช่น 101, 202' : 'ค้นหา HN, ชื่อ, หรือนามสกุล'}
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
            />
            {searchQuery.length > 0 && (
              <button onClick={() => { setSearchQuery(''); setSearchResults([]); setError(null); }}>
                <X size={18} className="sm:w-5 sm:h-5 text-gray-500" />
              </button>
            )}
          </div>
          {error && (
            <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {isSearching ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16">
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">กำลังค้นหา...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2 sm:space-y-3">
              {searchResults.map((patient) => (
                <button
                  key={patient.patient_id}
                  onClick={() => handleSelectPatient(patient)}
                  className="w-full flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white border rounded-xl hover:border-blue-500 hover:shadow-md transition-all text-left"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    {patient.profile_image ? (
                      <img src={patient.profile_image} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover" />
                    ) : (
                      <User size={24} className="sm:w-8 sm:h-8 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                      {patient.first_name} {patient.last_name}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600">
                      HN: {patient.hn} | {patient.age} ปี | {patient.gender}
                    </p>
                    {patient.room_number && patient.room_number !== '-' && (
                      <div className="flex items-center gap-1 text-xs text-blue-600 mt-0.5">
                        <Home size={12} />
                        <span>ห้อง: {patient.room_number}</span>
                      </div>
                    )}
                  </div>
                  <ChevronRight size={20} className="sm:w-6 sm:h-6 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : searchQuery.length >= 1 ? (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16">
              <Search size={48} className="sm:w-16 sm:h-16 text-gray-300" />
              <p className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold text-gray-600">ไม่พบผู้ป่วย</p>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 text-center px-4">
                ลองค้นหาด้วยคำอื่นหรือตรวจสอบความถูกต้อง
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16">
              <Search size={48} className="sm:w-16 sm:h-16 text-gray-300" />
              <p className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold text-gray-600">ค้นหาผู้ป่วย</p>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 text-center px-4">พิมพ์เพื่อเริ่มค้นหา</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// NurseSearch Component (ค้นหาพยาบาล/ผู้บันทึกด้วยชื่อ)
// ---------------------------------------------------------------------
function NurseSearch({ visible, onClose, onSelectNurse }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    setError(null);

    if (query.length < 1) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      // getUsersNurse คืนค่าเป็น array ตรงๆ ไม่ได้ห่อด้วย {success, data}
      const response = await api.get(`/users/nurse?search=${encodeURIComponent(query)}`);
      setSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Nurse search error:', err);
      setError('เกิดข้อผิดพลาดในการค้นหา กรุณาลองใหม่อีกครั้ง');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  }, [visible]);

  const handleSelect = (nurse) => {
    onSelectNurse(nurse);
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition">
            <X size={20} />
          </button>
          <h2 className="text-base sm:text-lg font-bold">ค้นหาพยาบาล/ผู้บันทึก</h2>
          <div className="w-10" />
        </div>

        <div className="p-3 sm:p-4 bg-gray-50 border-b">
          <div className="flex items-center gap-2 sm:gap-3 bg-white border rounded-xl px-3 sm:px-4 py-2.5 sm:py-3">
            <Search size={18} className="text-gray-500" />
            <input
              type="text"
              className="flex-1 outline-none text-sm sm:text-base"
              placeholder="พิมพ์ชื่อหรือนามสกุลพยาบาล"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
            />
            {searchQuery.length > 0 && (
              <button onClick={() => { setSearchQuery(''); setSearchResults([]); setError(null); }}>
                <X size={18} className="text-gray-500" />
              </button>
            )}
          </div>
          {error && (
            <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs sm:text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {isSearching ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
              <p className="mt-3 text-sm text-gray-600">กำลังค้นหา...</p>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-2">
              {searchResults.map((nurse) => (
                <button
                  key={nurse.user_id}
                  onClick={() => handleSelect(nurse)}
                  className="w-full flex items-center gap-3 p-3 bg-white border rounded-xl hover:border-purple-500 hover:shadow-md transition-all text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User size={20} className="text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">
                      {nurse.first_name} {nurse.last_name}
                    </p>
                    {nurse.position_name && <p className="text-xs text-gray-500">{nurse.position_name}</p>}
                  </div>
                  <ChevronRight size={18} className="text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : searchQuery.length >= 1 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Search size={40} className="text-gray-300" />
              <p className="mt-3 text-sm font-semibold text-gray-600">ไม่พบรายชื่อ</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <Search size={40} className="text-gray-300" />
              <p className="mt-3 text-sm text-gray-500">พิมพ์เพื่อเริ่มค้นหา</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

function formatThaiDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatShortTime(timeStr) {
  if (!timeStr) return '-';
  return timeStr.slice(0, 5);
}

function vitalsSummary(r) {
  const parts = [];
  if (r.temperature) parts.push(`T ${r.temperature}°C`);
  if (r.blood_pressure) parts.push(`BP ${r.blood_pressure}`);
  if (r.pulse) parts.push(`PR ${r.pulse}`);
  if (r.respiratory_rate) parts.push(`RR ${r.respiratory_rate}`);
  if (r.oxygen_saturation) parts.push(`SpO₂ ${r.oxygen_saturation}%`);
  if (r.blood_sugar) parts.push(`DTX ${r.blood_sugar}`);
  return parts.length ? parts.join(' · ') : 'ไม่ได้บันทึกสัญญาณชีพ';
}

function exportCsv(rows, filenamePrefix = 'procedure-history') {
  const header = ['วันที่', 'เวลา', 'เวร', 'HN', 'ชื่อ-นามสกุล', 'ประเภท', 'ห้อง', 'ผู้บันทึก', 'หัตถการ', 'สัญญาณชีพ'];
  const lines = rows.map((r) => [
    formatThaiDate(r.record_date),
    formatShortTime(r.record_time),
    r.shift ?? '',
    r.hn,
    `${r.first_name} ${r.last_name}`,
    r.record_type,
    r.room_number ?? '',
    r.created_by_name,
    (r.procedures || []).map((p) => p.display_name).join('; '),
    vitalsSummary(r),
  ]);
  const csv = [header, ...lines]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenamePrefix}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------

const RECORD_TYPE_STYLE = {
  OPD: 'bg-green-50 text-green-700 border-green-300',
  IPD: 'bg-blue-50 text-blue-700 border-blue-300',
};

function RecordTypeTag({ type }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${RECORD_TYPE_STYLE[type] || 'bg-gray-50 text-gray-600 border-gray-300'}`}>
      {type}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, colorClass }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

/** แผนผังจุดตำแหน่งแผล แบบย่อ (front/back) จาก x_pct / y_pct */
function WoundMiniMap({ markers, view }) {
  const points = markers.filter((m) => m.view === view);
  return (
    <div className="flex flex-col items-center gap-1">
      <svg viewBox="0 0 100 180" width={64} height={115} className="rounded-lg bg-white border border-purple-100">
        <ellipse cx="50" cy="16" rx="12" ry="13" fill="none" stroke="#D8D2E8" strokeWidth="1.5" />
        <path d="M32 30 Q50 24 68 30 L74 90 Q60 100 50 100 Q40 100 26 90 Z" fill="none" stroke="#D8D2E8" strokeWidth="1.5" />
        <path d="M32 30 L18 70 M68 30 L82 70" fill="none" stroke="#D8D2E8" strokeWidth="1.5" />
        <path d="M40 100 L36 165 M60 100 L64 165" fill="none" stroke="#D8D2E8" strokeWidth="1.5" />
        {points.map((m) => (
          <circle key={m.id} cx={m.x_pct} cy={(m.y_pct / 100) * 180} r={3.2} fill="#9333ea">
            <title>{m.note ?? 'จุดที่บันทึก'}</title>
          </circle>
        ))}
      </svg>
      <span className="text-[11px] text-gray-500">{view === 'front' ? 'ด้านหน้า' : 'ด้านหลัง'}</span>
    </div>
  );
}

// ---------------------------------------------------------------------
// Detail panel (แถวขยาย)
// ---------------------------------------------------------------------

function DetailPanel({ record }) {
  const hasWoundMarkers = (record.wound_markers || []).length > 0;
  const procedures = record.procedures || [];
  const nonChargeable = record.non_chargeable_procedures || [];
  const supplies = record.medical_supplies || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 bg-gray-50 border-t p-4 sm:p-5">
      {/* อาการ / สัญญาณชีพ */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Activity size={16} className="text-green-700" />
          <h4 className="text-sm font-semibold text-green-800">อาการและสัญญาณชีพ</h4>
        </div>
        {record.chief_complaint && (
          <p className="text-sm text-gray-700 mb-1"><span className="text-gray-500">อาการสำคัญ: </span>{record.chief_complaint}</p>
        )}
        {record.chronic_disease && (
          <p className="text-sm text-gray-700 mb-1"><span className="text-gray-500">โรคประจำตัว: </span>{record.chronic_disease}</p>
        )}
        <p className="text-sm text-gray-700">{vitalsSummary(record)}</p>
        {record.appointment_date && (
          <p className="text-sm text-gray-700 mt-1"><span className="text-gray-500">นัดครั้งถัดไป: </span>{formatThaiDate(record.appointment_date)}</p>
        )}
        {record.remark && (
          <p className="mt-2 rounded-lg bg-white border border-green-200 px-2.5 py-1.5 text-sm text-gray-700">{record.remark}</p>
        )}
      </div>

      {/* หัตถการ / เวชภัณฑ์ */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Stethoscope size={16} className="text-purple-700" />
          <h4 className="text-sm font-semibold text-purple-800">หัตถการที่ทำ</h4>
        </div>
        {procedures.length === 0 && nonChargeable.length === 0 ? (
          <p className="text-sm text-gray-500">ไม่มีรายการหัตถการ</p>
        ) : (
          <ul className="mb-3 space-y-1">
            {procedures.map((p) => (
              <li key={`p-${p.id}`} className="text-sm text-gray-700">
                • {p.display_name}
                {p.sub_option_value ? ` (${p.sub_option_value})` : ''}
                {p.performed_by ? <span className="text-gray-500"> — {p.performed_by}</span> : null}
              </li>
            ))}
            {nonChargeable.map((p) => (
              <li key={`ncp-${p.id}`} className="text-sm text-gray-500">
                • {p.procedure_name} <span className="text-[11px]">(ไม่คิดค่าใช้จ่าย)</span>
              </li>
            ))}
          </ul>
        )}

        {supplies.length > 0 && (
          <>
            <p className="text-xs font-semibold text-purple-700 mb-1 mt-2">เวชภัณฑ์ที่ใช้</p>
            <table className="w-full text-sm">
              <tbody>
                {supplies.map((s) => (
                  <tr key={s.procedure_supply_id} className="border-b border-purple-100 last:border-0">
                    <td className="py-1 pr-2 text-gray-700">{s.supply_name}</td>
                    <td className="py-1 text-right text-gray-500 whitespace-nowrap">{s.quantity_used} {s.unit_name ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ตำแหน่งแผล / รูปภาพ */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <MapPin size={16} className="text-blue-700" />
          <h4 className="text-sm font-semibold text-blue-800">ตำแหน่งแผล</h4>
        </div>
        {hasWoundMarkers ? (
          <div className="flex gap-3">
            <WoundMiniMap markers={record.wound_markers} view="front" />
            <WoundMiniMap markers={record.wound_markers} view="back" />
          </div>
        ) : (
          <p className="text-sm text-gray-500">ไม่มีการปักหมุดตำแหน่งแผล</p>
        )}
        {record.image_count > 0 && (
          <div className="mt-3 flex items-center gap-1.5 text-sm text-blue-700">
            <ImageIcon size={14} />
            <span>แนบรูปภาพ {record.image_count} รูป</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Nurse work summary card (สรุปรายปี + หัตถการที่ทำบ่อย)
// ---------------------------------------------------------------------

function NurseSummaryPanel({ nurse, summary, loading, yearFilter, onSelectYear, yearOptions }) {
  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0">
            <User size={24} className="text-purple-700" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-purple-900 text-lg">
              {nurse.first_name} {nurse.last_name}
            </p>
            {summary?.summary && (
              <p className="text-sm text-purple-700">
                บันทึกทั้งหมด {summary.summary.total_all_time} รายการ
                {summary.summary.first_record && (
                  <> · ตั้งแต่ {formatThaiDate(summary.summary.first_record)}</>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* เลือกช่วงเวลา */}
      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm font-medium text-gray-700">ช่วงเวลา:</label>
        <select
          value={yearFilter}
          onChange={(e) => onSelectYear(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="">ตลอดที่เข้าทำงาน (ทุกปี)</option>
          {yearOptions.map((y) => (
            <option key={y} value={y}>ปี {y + 543}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">กำลังโหลดสรุป...</p>
      ) : summary?.byYear?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="overflow-x-auto">
            <p className="text-xs font-semibold text-gray-500 mb-1">สรุปรายปี</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-1 pr-3">ปี</th>
                  <th className="py-1 pr-3">OPD</th>
                  <th className="py-1 pr-3">IPD</th>
                  <th className="py-1">รวม</th>
                </tr>
              </thead>
              <tbody>
                {summary.byYear.map((y) => (
                  <tr
                    key={y.year}
                    className={`border-b last:border-0 cursor-pointer hover:bg-purple-50 ${
                      String(y.year) === yearFilter ? 'bg-purple-50 font-semibold' : ''
                    }`}
                    onClick={() => onSelectYear(String(y.year))}
                  >
                    <td className="py-1.5 pr-3">{y.year + 543}</td>
                    <td className="py-1.5 pr-3 text-green-700">{y.opd_count}</td>
                    <td className="py-1.5 pr-3 text-blue-700">{y.ipd_count}</td>
                    <td className="py-1.5 font-medium">{y.total_records}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {summary?.topProcedures?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">หัตถการที่ทำบ่อยที่สุด (ตลอดการทำงาน)</p>
              <ul className="space-y-1">
                {summary.topProcedures.map((p, idx) => (
                  <li key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-700 truncate pr-2">{p.display_name}</span>
                    <span className="text-purple-700 font-medium flex-shrink-0">{p.count} ครั้ง</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500">ไม่พบข้อมูลการบันทึกของพยาบาลคนนี้</p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------

export default function ProcedureHistoryReport() {
  const { user } = useAuth();
  const { hn } = useParams();

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);

  // 🆕 พยาบาล/ผู้บันทึก
  const [showNurseSearchModal, setShowNurseSearchModal] = useState(false);
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [yearFilter, setYearFilter] = useState(''); // '' = ทั้งหมด/ตลอดที่เข้าทำงาน
  const [nurseSummary, setNurseSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pendingFilters, setPendingFilters] = useState(EMPTY_FILTERS);
  const [onlyMine, setOnlyMine] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // สร้าง list ปีย้อนหลัง 6 ปี ให้เลือก
  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => current - i);
  }, []);

  // ถ้าเปิดหน้าจาก route ที่มี HN (เช่นมาจากหน้าโปรไฟล์ผู้ป่วย) ให้โหลดผู้ป่วยและตั้งฟิลเตอร์อัตโนมัติ
  useEffect(() => {
    if (!hn) return;
    (async () => {
      try {
        setIsLoadingPatient(true);
        const response = await api.get(`/service-registrations/hn/${hn}`);
        setSelectedPatient(response.data.data);
      } catch (err) {
        console.error('Load patient by HN error:', err);
        alert(`ไม่พบข้อมูลผู้ป่วย HN: ${hn}`);
      } finally {
        setIsLoadingPatient(false);
      }
    })();
  }, [hn]);

  // ใช้ร่วมกันทั้งตอนโหลดตาราง (มี pagination) และตอนส่งออก CSV (ไม่มี pagination)
  const buildQueryParams = useCallback((activeFilters, patient, mineOnly, nurse, year) => {
    const params = {};
    if (activeFilters.shift) params.shift = activeFilters.shift;
    if (activeFilters.recordType) params.recordType = activeFilters.recordType;
    if (activeFilters.startDate) params.startDate = activeFilters.startDate;
    if (activeFilters.endDate) params.endDate = activeFilters.endDate;
    if (patient) params.patientId = patient.patient_id;
    if (mineOnly && user?.user_id) params.createdBy = user.user_id;
    if (nurse) params.createdBy = nurse.user_id; // เลือกพยาบาลจากการค้นหาแล้ว ใช้ id ตรงๆ
    if (year) params.year = year;
    return params;
  }, [user]);

  const fetchRecords = useCallback(async (activeFilters, pageIndex, patient, mineOnly, nurse, year) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        ...buildQueryParams(activeFilters, patient, mineOnly, nurse, year),
        limit: PAGE_SIZE,
        offset: pageIndex * PAGE_SIZE,
      };

      const response = await api.get('/procedure-records', { params });

      if (response.data.success === false) {
        throw new Error(response.data.message || 'โหลดข้อมูลไม่สำเร็จ');
      }
      setRows(response.data.data || []);
      setTotal(response.data.pagination?.total ?? response.data.total ?? 0);
    } catch (err) {
      console.error('Load procedure records error:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchRecords(filters, page, selectedPatient, onlyMine, selectedNurse, yearFilter);
  }, [filters, page, selectedPatient, onlyMine, selectedNurse, yearFilter, fetchRecords]);

  // 🆕 โหลดสรุปการทำงานทั้งหมด (ทุกปี) เมื่อเลือกพยาบาล
  useEffect(() => {
    if (!selectedNurse) {
      setNurseSummary(null);
      return;
    }
    (async () => {
      try {
        setLoadingSummary(true);
        const res = await api.get(`/procedure-records/user/${selectedNurse.user_id}/summary`);
        setNurseSummary(res.data.data);
      } catch (err) {
        console.error('Load nurse summary error:', err);
        setNurseSummary(null);
      } finally {
        setLoadingSummary(false);
      }
    })();
  }, [selectedNurse]);

  const stats = useMemo(() => {
    const opd = rows.filter((r) => r.record_type === 'OPD').length;
    const ipd = rows.filter((r) => r.record_type === 'IPD').length;
    const withImages = rows.filter((r) => r.image_count > 0).length;
    return { opd, ipd, withImages };
  }, [rows]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPage(0);
  };

  const clearPatientFilter = () => {
    setSelectedPatient(null);
    setPage(0);
  };

  // 🆕
  const handleSelectNurse = (nurse) => {
    setSelectedNurse(nurse);
    setYearFilter('');
    setPage(0);
  };

  const clearNurseFilter = () => {
    setSelectedNurse(null);
    setYearFilter('');
    setNurseSummary(null);
    setPage(0);
  };

  const handleSelectYear = (year) => {
    setYearFilter(year);
    setPage(0);
  };

  const applyFilters = () => {
    setPage(0);
    setFilters(pendingFilters);
  };

  const resetFilters = () => {
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setOnlyMine(false);
    setPage(0);
  };

  const exportFilenamePrefix = selectedNurse
    ? `procedure-history-${selectedNurse.first_name}-${selectedNurse.last_name}`
    : 'procedure-history';

  // 🆕 ส่งออก CSV ทั้งหมดที่ตรงกับตัวกรองปัจจุบัน ไม่ใช่แค่หน้าที่กำลังแสดง
  const handleExportAll = async () => {
    setIsExporting(true);
    try {
      const params = {
        ...buildQueryParams(filters, selectedPatient, onlyMine, selectedNurse, yearFilter),
        limit: total > 0 ? total : 100000, // ดึงให้ครบตามจำนวนจริงที่นับได้
        offset: 0,
      };
      const response = await api.get('/procedure-records', { params });
      if (response.data.success === false) {
        throw new Error(response.data.message || 'ส่งออกข้อมูลไม่สำเร็จ');
      }
      const allRows = response.data.data || [];
      if (allRows.length === 0) {
        alert('ไม่พบข้อมูลสำหรับส่งออก');
        return;
      }
      exportCsv(allRows, exportFilenamePrefix);
    } catch (err) {
      console.error('Export all CSV error:', err);
      alert('เกิดข้อผิดพลาดในการส่งออก CSV กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-3">
            <ClipboardList size={28} className="text-blue-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">รายงานประวัติการทำหัตถการ</h1>
              <p className="text-sm text-gray-600">สำหรับพยาบาลและผู้ช่วยพยาบาลตรวจสอบรายการหัตถการที่บันทึกไว้</p>
            </div>
          </div>
          <button
            onClick={handleExportAll}
            disabled={total === 0 || isExporting}
            className="flex items-center justify-center gap-2 bg-purple-50 hover:bg-purple-100 disabled:opacity-40 disabled:cursor-not-allowed text-purple-600 font-semibold py-2.5 px-4 rounded-lg border border-purple-600 transition"
          >
            {isExporting ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            ) : (
              <FileDown size={18} />
            )}
            {isExporting ? `กำลังส่งออก...` : `ส่งออก CSV (${total} รายการ)`}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ผู้ป่วย (ตัวกรองเฉพาะราย) */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">กรองตามผู้ป่วย (ไม่บังคับ)</h2>
            <button
              onClick={() => setShowSearchModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 font-semibold rounded-lg border border-blue-600 transition"
            >
              <Search size={20} />
              ค้นหาผู้ป่วย
            </button>
          </div>

          {isLoadingPatient ? (
            <div className="flex flex-col items-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="mt-3 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          ) : selectedPatient ? (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                  <User size={24} className="text-blue-700" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-blue-900 text-lg">
                    {selectedPatient.first_name} {selectedPatient.last_name}
                  </p>
                  <p className="text-sm text-blue-700">
                    HN: {selectedPatient.hn} {selectedPatient.age ? `| อายุ: ${selectedPatient.age} ปี` : ''} {selectedPatient.gender ? `| เพศ: ${selectedPatient.gender}` : ''}
                  </p>
                </div>
                <button onClick={clearPatientFilter} className="p-2 hover:bg-blue-100 rounded-lg transition">
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">แสดงหัตถการของผู้ป่วยทุกคน — กดค้นหาเพื่อกรองเฉพาะผู้ป่วยรายหนึ่ง</p>
          )}
        </div>

        {/* 🆕 พยาบาล/ผู้บันทึก (ตัวกรองเฉพาะราย + สรุปการทำงาน) */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">กรองตามพยาบาลผู้บันทึก (ไม่บังคับ)</h2>
            <button
              onClick={() => setShowNurseSearchModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-600 font-semibold rounded-lg border border-purple-600 transition"
            >
              <User size={20} />
              ค้นหาพยาบาล
            </button>
          </div>

          {selectedNurse ? (
            <>
              <div className="flex justify-end mb-2">
                <button onClick={clearNurseFilter} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
                  <X size={14} />
                  ล้างตัวกรองพยาบาล
                </button>
              </div>
              <NurseSummaryPanel
                nurse={selectedNurse}
                summary={nurseSummary}
                loading={loadingSummary}
                yearFilter={yearFilter}
                onSelectYear={handleSelectYear}
                yearOptions={yearOptions}
              />
            </>
          ) : (
            <p className="text-sm text-gray-500">แสดงหัตถการของทุกคน — กดค้นหาเพื่อกรองเฉพาะพยาบาลรายหนึ่ง และดูสรุปการทำงานทั้งปีหรือตลอดที่เข้าทำงาน</p>
          )}
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <StatCard icon={ClipboardList} label="รายการทั้งหมด" value={total} colorClass="bg-gray-100 text-gray-600" />
          <StatCard icon={Stethoscope} label="ผู้ป่วยนอก (OPD)" value={stats.opd} colorClass="bg-green-100 text-green-600" />
          <StatCard icon={Activity} label="ผู้ป่วยใน (IPD)" value={stats.ipd} colorClass="bg-blue-100 text-blue-600" />
          <StatCard icon={ImageIcon} label="มีรูปภาพแนบ" value={stats.withImages} colorClass="bg-purple-100 text-purple-600" />
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-gray-500" />
            <h2 className="text-lg font-bold text-gray-900">ตัวกรอง</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">ประเภทเคส</label>
              <select
                value={pendingFilters.recordType}
                onChange={(e) => setPendingFilters((f) => ({ ...f, recordType: e.target.value }))}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">ทั้งหมด</option>
                <option value="OPD">OPD</option>
                <option value="IPD">IPD</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">เวร</label>
              <select
                value={pendingFilters.shift}
                onChange={(e) => setPendingFilters((f) => ({ ...f, shift: e.target.value }))}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">ทั้งหมด</option>
                <option value="D">D (กลางวัน)</option>
                <option value="E">E (เย็น)</option>
                <option value="N">N (กลางคืน)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">วันที่เริ่ม</label>
              <input
                type="date"
                value={pendingFilters.startDate}
                onChange={(e) => setPendingFilters((f) => ({ ...f, startDate: e.target.value }))}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">วันที่สิ้นสุด</label>
              <input
                type="date"
                value={pendingFilters.endDate}
                onChange={(e) => setPendingFilters((f) => ({ ...f, endDate: e.target.value }))}
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            แสดงเฉพาะรายการที่ฉันบันทึก
          </label>

          <div className="flex gap-2 mt-4">
            <button
              onClick={applyFilters}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-lg transition"
            >
              <Search size={18} />
              ค้นหา
            </button>
            <button
              onClick={resetFilters}
              className="flex items-center justify-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-600 font-semibold py-2.5 px-5 rounded-lg border border-gray-300 transition"
            >
              <RotateCcw size={18} />
              ล้างค่า
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <Info size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* รายการ */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="mt-3 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <ClipboardList size={48} className="text-gray-300" />
              <p className="mt-4 text-lg font-semibold text-gray-600">ไม่พบรายการหัตถการ</p>
              <p className="mt-2 text-sm text-gray-500">ลองปรับตัวกรองหรือช่วงวันที่ดูใหม่</p>
            </div>
          ) : (
            <div className="divide-y">
              {rows.map((r) => {
                const isOpen = expandedId === r.id;
                const procedureNames = [
                  ...(r.procedures || []).map((p) => p.display_name),
                  ...(r.non_chargeable_procedures || []).map((p) => p.procedure_name),
                ];
                return (
                  <div key={r.id}>
                    <button
                      onClick={() => setExpandedId(isOpen ? null : r.id)}
                      className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 px-4 sm:px-6 py-4 hover:bg-gray-50 transition text-left"
                    >
                      <div className="flex items-center gap-2 sm:w-16 flex-shrink-0 text-gray-400">
                        {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                      </div>

                      <div className="sm:w-32 flex-shrink-0">
                        <p className="text-sm font-medium text-gray-900">{formatThaiDate(r.record_date)}</p>
                        <p className="text-xs text-gray-500">{formatShortTime(r.record_time)}{r.shift ? ` · เวร${r.shift}` : ''}</p>
                      </div>

                      <div className="sm:w-48 flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{r.first_name} {r.last_name}</p>
                        <p className="text-xs text-gray-500">HN: {r.hn}</p>
                      </div>

                      <div className="sm:w-32 flex-shrink-0 flex flex-col gap-1">
                        <RecordTypeTag type={r.record_type} />
                        {r.room_number && <span className="text-xs text-gray-500">ห้อง {r.room_number}</span>}
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">
                          {procedureNames.length > 0 ? procedureNames.join(', ') : '—'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{vitalsSummary(r)}</p>
                      </div>

                      <div className="sm:w-32 flex-shrink-0 text-xs text-gray-500 text-right">
                        {r.created_by_name}
                      </div>
                    </button>

                    {isOpen && <DetailPanel record={r} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>หน้า {page + 1} จาก {totalPages} — ทั้งหมด {total} รายการ</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || loading}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                ก่อนหน้า
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || loading}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
              >
                ถัดไป
              </button>
            </div>
          </div>
        )}
      </div>

      <PatientSearch
        visible={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelectPatient={handleSelectPatient}
      />

      <NurseSearch
        visible={showNurseSearchModal}
        onClose={() => setShowNurseSearchModal(false)}
        onSelectNurse={handleSelectNurse}
      />
    </div>
  );
}